import { PlanType } from '~/generated/api-clients-generated'
import type { SupportNumber } from '~/types/cms'

export type SupportNumberContext = {
  path: string
  planType?: PlanType | null
  journeyId?: number | null
  environment?: string | null
}

// A path pattern is either exact ("/drugs") or a subtree ("/iep/*"), which matches
// "/iep" and everything below it. "/*" therefore matches every path.
// Returns how specific the match is, or null for no match.
function pathSpecificity(pattern: string, path: string): number | null {
  if (!pattern.endsWith('/*')) return pattern === path ? Number.MAX_SAFE_INTEGER : null

  const base = pattern.slice(0, -2)
  if (base !== '' && path !== base && !path.startsWith(base + '/')) return null
  return base.split('/').length
}

const matchesEnvironment = (rule: SupportNumber, environment?: string | null) => {
  const environments = rule.rule?.environments ?? []
  return (
    environments.length === 0 ||
    (!_isNil(environment) &&
      environments.some((e) => e.toLowerCase() === environment.toLowerCase()))
  )
}

function specificity(rule: SupportNumber, ctx: SupportNumberContext): number[] | null {
  const paths = rule.rule?.paths ?? []
  const planTypes = rule.rule?.planTypes ?? []
  const journeys = rule.rule?.journeys ?? []
  const environments = rule.rule?.environments ?? []

  if (!matchesEnvironment(rule, ctx.environment)) return null

  const pathScores = paths
    .map((p) => pathSpecificity(p, ctx.path))
    .filter((s): s is number => s !== null)
  if (pathScores.length === 0) return null

  const planTypeName = _isNil(ctx.planType) ? null : PlanType[ctx.planType]
  if (planTypes.length > 0 && (!planTypeName || !planTypes.includes(planTypeName))) return null

  if (journeys.length > 0 && (_isNil(ctx.journeyId) || !journeys.includes(String(ctx.journeyId))))
    return null

  // Compared in order: path first, then journey-scoped over global, then plan-type-scoped over
  // any, then environment-scoped over all, so a row being trialled in QA overrides its twin.
  return [
    Math.max(...pathScores),
    journeys.length > 0 ? 1 : 0,
    planTypes.length > 0 ? 1 : 0,
    environments.length > 0 ? 1 : 0
  ]
}

function compare(a: number[], b: number[]) {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] - b[i]
  return 0
}

/**
 * Picks the most specific rule matching the context. A matching rule with a null number
 * hides the support number. Ties go to the earlier rule, i.e. Directus sort order.
 *
 * Returns undefined when no rule matches, null when the number should be hidden.
 */
export function resolveSupportNumber(
  rules: SupportNumber[],
  ctx: SupportNumberContext
): string | null | undefined {
  let best: { rule: SupportNumber; score: number[] } | undefined

  for (const rule of rules) {
    const score = specificity(rule, ctx)
    if (score && (!best || compare(score, best.score) > 0)) best = { rule, score }
  }

  return best ? best.rule.number : undefined
}

/**
 * The number on the catch-all rule ("/*", no plan types, no journeys) for this environment,
 * preferring an environment-scoped one.
 */
export function defaultSupportNumber(
  rules: SupportNumber[],
  environment?: string | null
): string | null | undefined {
  const catchAlls = rules.filter(
    (r) =>
      r.rule?.paths?.includes('/*') &&
      _isEmpty(r.rule?.planTypes) &&
      _isEmpty(r.rule?.journeys) &&
      !_isNil(r.number) &&
      matchesEnvironment(r, environment)
  )
  return (catchAlls.find((r) => !_isEmpty(r.rule?.environments)) ?? catchAlls[0])?.number
}
